import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { subscribeToToasts, toast, toastError, toastInfo, toastSuccess, toastWarning, ToastEvent, ToastVariant } from '../utils/toast';

type ToastContextValue = {
	toast: typeof toast;
	toastSuccess: typeof toastSuccess;
	toastError: typeof toastError;
	toastInfo: typeof toastInfo;
	toastWarning: typeof toastWarning;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast() {
	const ctx = useContext(ToastContext);
	if (!ctx) throw new Error('useToast must be used within ToastProvider');
	return ctx;
}

type LocalToast = ToastEvent & { expiresAt: number };

const variantStyles: Record<ToastVariant, string> = {
	success: 'bg-green-600 text-white',
	error: 'bg-red-600 text-white',
	info: 'bg-blue-600 text-white',
	warning: 'bg-yellow-600 text-white',
};

export const ToastProvider: React.FC<{ children: React.ReactNode }>= ({ children }) => {
	const [toasts, setToasts] = useState<LocalToast[]>([]);
	const timerRef = useRef<number | null>(null);

	useEffect(() => {
		const unsubscribe = subscribeToToasts((evt) => {
			setToasts((existing) => [
				...existing,
				{ ...evt, expiresAt: Date.now() + (evt.duration ?? 3500) },
			]);
		});
		return unsubscribe;
	}, []);

	useEffect(() => {
		if (timerRef.current) window.clearInterval(timerRef.current);
		timerRef.current = window.setInterval(() => {
			setToasts((existing) => existing.filter((t) => t.expiresAt > Date.now()));
		}, 200) as unknown as number;
		return () => {
			if (timerRef.current) window.clearInterval(timerRef.current);
		};
	}, []);

	const ctxValue = useMemo<ToastContextValue>(() => ({
		toast,
		toastSuccess,
		toastError,
		toastInfo,
		toastWarning,
	}), []);

	return (
		<ToastContext.Provider value={ctxValue}>
			{children}
			<div className="fixed z-50 top-4 right-4 space-y-2 w-96 max-w-[90vw]">
				{toasts.map((t) => (
					<div key={t.id} className={`shadow-lg rounded-md px-4 py-3 flex items-start gap-3 ${variantStyles[t.variant]}`}>
						<div className="flex-1">
							{t.title && <div className="font-semibold leading-tight">{t.title}</div>}
							<div className="text-sm leading-snug">{t.message}</div>
						</div>
					</div>
				))}
			</div>
		</ToastContext.Provider>
	);
};

export default ToastProvider;


